import { useState, useEffect, useCallback } from "react";

/**
 * usePersistedState Hook
 *
 * State hook that persists to localStorage or sessionStorage
 *
 * @param {string} key - Storage key
 * @param {any} initialValue - Initial value if not in storage
 * @param {Object} options - Configuration options
 * @param {string} options.storage - 'local' or 'session' (default: 'local')
 * @param {Function} options.serialize - Custom serializer (default: JSON.stringify)
 * @param {Function} options.deserialize - Custom deserializer (default: JSON.parse)
 * @returns {[any, Function, Function]} [value, setValue, removeValue]
 *
 * @example
 * const [viewMode, setViewMode, clearViewMode] = usePersistedState(
 *   'propertyViewMode',
 *   'cards',
 *   { storage: 'session' }
 * );
 */
export function usePersistedState(
  key,
  initialValue,
  options = {}
) {
  const {
    storage = "local",
    serialize = JSON.stringify,
    deserialize = JSON.parse,
  } = options;

  // If storage is 'none', just use regular useState
  const isPersisted = storage && storage !== "none";

  // Get storage object (only if persisting)
  const storageObject = isPersisted
    ? storage === "session"
      ? sessionStorage
      : localStorage
    : null;

  // Initialize state from storage or use initial value
  const [state, setState] = useState(() => {
    if (!isPersisted) return initialValue;

    try {
      const item = storageObject.getItem(key);
      return item ? deserialize(item) : initialValue;
    } catch (error) {
      console.warn(`Error reading from ${storage}Storage for key "${key}":`, error);
      return initialValue;
    }
  });

  // Update storage when state changes (only if persisting)
  useEffect(() => {
    if (!isPersisted) return;

    try {
      if (state === null || state === undefined) {
        storageObject.removeItem(key);
      } else {
        storageObject.setItem(key, serialize(state));
      }
    } catch (error) {
      console.warn(`Error writing to ${storage}Storage for key "${key}":`, error);
    }
  }, [key, state, storage, storageObject, serialize, isPersisted]);

  // Remove value from storage
  const removeValue = useCallback(() => {
    if (!isPersisted) {
      setState(initialValue);
      return;
    }

    try {
      storageObject.removeItem(key);
      setState(initialValue);
    } catch (error) {
      console.warn(`Error removing from ${storage}Storage for key "${key}":`, error);
    }
  }, [key, initialValue, storage, storageObject, isPersisted]);

  return [state, setState, removeValue];
}

export default usePersistedState;
